'use client';

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { Button } from '../../../components/ui/Button';
import { buttonVariants } from '../../../components/ui/button-variants';
import { Divider } from '../../../components/ui/Divider';
import { Link } from '../../../components/ui/Link';
import { ROUTES } from '../../../constants/routes';
import {
  setShelterAnalyticsContext,
  trackQuizComplete,
  trackQuizPageView,
  trackQuizPreferencesFromProfile,
  trackQuizStart,
} from '../../../lib/analytics';
import { cn } from '../../../lib/utils';
import {
  getMatchApiErrorMessage,
  getMatchQuestionsErrorMessage,
  type MatchApiSuccessData,
} from '../api/match-api';
import { ImportanceSegmentedControl } from '../components/ImportanceSegmentedControl';
import { MatchQuizFooterNav } from '../components/MatchQuizFooterNav';
import { MatchQuizOptionCard } from '../components/MatchQuizOptionCard';
import { MatchQuizProgress } from '../components/MatchQuizProgress';
import { MatchQuizResultsView } from '../components/MatchQuizResultsView';
import { MatchQuizSpeciesGate } from '../components/MatchQuizSpeciesGate';
import { MatchQuizStepIllustration } from '../components/MatchQuizStepIllustration';
import { useMatchQuestionsQuery } from '../hooks/useMatchQuestionsQuery';
import { usePostMatchMutation } from '../hooks/usePostMatchMutation';
import { animalBehaviorVector } from '../lib/animalBehaviorVector';
import { matchQuizOptionTitleDescription } from '../lib/matchQuizOptionText';
import type { SpeciesQuizPair } from '../lib/parseSpeciesQuizCsv';
import {
  scrollIntoViewPreferred,
  scrollToDocumentTopImmediate,
} from '../lib/scrollIntoViewPreferred';
import {
  buildMatchApiProfile,
  defaultUserMatchProfile,
  isQuizStepComplete,
  profileToSerializable,
  type UserMatchProfile,
} from '../lib/userMatchProfile';
import type { ImportanceLevel } from '../lib/importanceWeights';
import { type DimensionKey, type RankedAnimal } from '../types';

/** First unanswered control in visual order: detail options, then importance. */
function quizScrollTargetId(
  pair: SpeciesQuizPair,
  profile: UserMatchProfile,
  petType: 'Cat' | 'Dog',
): string | null {
  const dim = profile[pair.dimensionKey];
  const detailSatisfied =
    dim.detailLevel != null &&
    pair.detailOptions.some((opt) => opt.level === dim.detailLevel);
  if (!detailSatisfied) {
    return `match-poc-detail-anchor-${petType}-${pair.dimensionKey}`;
  }
  if (dim.importance == null) {
    return `match-poc-importance-anchor-${petType}-${pair.dimensionKey}`;
  }
  return null;
}

/** Stable id on the quiz footer primary button — scroll target and e2e hooks. */
export const MATCH_POC_QUIZ_FOOTER_PRIMARY_ID = 'match-poc-quiz-footer-primary';

const showMatchPocDebugPanel =
  process.env.NODE_ENV === 'development' ||
  process.env.NEXT_PUBLIC_MATCH_POC_DEBUG === 'true';
const autoStepComplete = true;

type MatchQuizPhase = 'species' | 'quiz' | 'results';

export type ShelterMatchQuizContext = {
  shelterId: string;
  /** Optional label from a future public shelter API. */
  shelterDisplayName?: string | null;
  /** External adoption application form URL when configured on the shelter. */
  shelterAdoptionFormUrl?: string | null;
};

export type MatchingPocPageProps = {
  /** When set, POST /match includes `shelterId` so results are scoped to that shelter’s roster. */
  shelterMatchContext?: ShelterMatchQuizContext | null;
};

export function MatchingPocPage({
  shelterMatchContext = null,
}: MatchingPocPageProps = {}) {
  const pathname = usePathname() ?? ROUTES.matchingPoc;
  const quizPageViewTracked = useRef(false);
  const [phase, setPhase] = useState<MatchQuizPhase>('species');
  const [petType, setPetType] = useState<'Cat' | 'Dog'>('Dog');
  const [profile, setProfile] = useState<UserMatchProfile>(
    defaultUserMatchProfile(),
  );
  const [results, setResults] = useState<RankedAnimal[] | null>(null);
  const [matchApiPayload, setMatchApiPayload] =
    useState<MatchApiSuccessData | null>(null);
  const [actionStatus, setActionStatus] = useState<string | null>(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [lastMatchResultsId, setLastMatchResultsId] = useState<string | null>(
    null,
  );

  const shelterIdForMatch = shelterMatchContext?.shelterId;
  const shelterSpeciesGateNote = shelterMatchContext
    ? shelterMatchContext.shelterDisplayName?.trim()
      ? `You’re viewing available pets at ${shelterMatchContext.shelterDisplayName.trim()}. Matches are ranked within that shelter only.`
      : 'Matches are ranked within this shelter’s available pets only (not site-wide).'
    : null;

  useEffect(() => {
    setShelterAnalyticsContext(shelterIdForMatch);
  }, [shelterIdForMatch]);

  useEffect(() => {
    if (quizPageViewTracked.current) return;
    quizPageViewTracked.current = true;
    trackQuizPageView(pathname, shelterIdForMatch);
  }, [pathname, shelterIdForMatch]);

  const questionsQuery = useMatchQuestionsQuery(petType, {
    enabled: phase === 'quiz',
  });
  const matchMutation = usePostMatchMutation();

  const speciesPairs = questionsQuery.data?.pairs ?? null;
  const questionsMeta = useMemo(
    () =>
      questionsQuery.data
        ? {
            schema: questionsQuery.data.schema,
            sourceBundle: questionsQuery.data.source.bundle,
          }
        : null,
    [questionsQuery.data],
  );
  const questionsError = questionsQuery.error
    ? getMatchQuestionsErrorMessage(questionsQuery.error)
    : null;
  const isLoadingQuestions = questionsQuery.isLoading;

  const totalSteps = speciesPairs?.length ?? 0;
  const currentPair =
    speciesPairs && speciesPairs.length > 0
      ? (speciesPairs[Math.min(currentStepIndex, speciesPairs.length - 1)] ??
        null)
      : null;

  useEffect(() => {
    if (!speciesPairs?.length) return;
    setCurrentStepIndex((i) => Math.min(i, speciesPairs.length - 1));
  }, [speciesPairs]);

  /**
   * Quiz scroll / question focus:
   * - Scroll ids sit on **prompt-only** wrappers (not the answer controls), so programmatic
   *   scroll aligns to the question text.
   * - Prompts use `scrollIntoView` with `block: 'center'` so the headline stays in comfortable
   *   view; answer cards sit below. Footer primary uses `block: 'start'` for the sticky bar.
   * - `scroll-margin-top` keeps prompts clear of the viewport edge.
   */
  const prevQuizStepKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (phase !== 'quiz') {
      prevQuizStepKeyRef.current = null;
      return;
    }
    if (!currentPair || questionsError || isLoadingQuestions) {
      return;
    }

    const stepKey = `${petType}-${currentPair.dimensionKey}-${currentStepIndex}`;
    const prev = prevQuizStepKeyRef.current;
    const isStepEntry = prev !== stepKey;
    prevQuizStepKeyRef.current = stepKey;

    let targetId: string;
    if (isStepEntry) {
      targetId = `match-poc-detail-anchor-${petType}-${currentPair.dimensionKey}`;
    } else if (isQuizStepComplete(currentPair, profile)) {
      targetId = MATCH_POC_QUIZ_FOOTER_PRIMARY_ID;
    } else {
      const next = quizScrollTargetId(currentPair, profile, petType);
      targetId = next ?? MATCH_POC_QUIZ_FOOTER_PRIMARY_ID;
    }

    const frame = requestAnimationFrame(() => {
      const el = document.getElementById(targetId);
      const block =
        targetId === MATCH_POC_QUIZ_FOOTER_PRIMARY_ID ? 'start' : 'center';
      scrollIntoViewPreferred(el, { block });
    });
    return () => cancelAnimationFrame(frame);
  }, [
    phase,
    currentPair,
    currentStepIndex,
    profile,
    petType,
    questionsError,
    isLoadingQuestions,
  ]);

  /**
   * Results + share flow: window must start at document top so users read the
   * header first and scroll down. `useLayoutEffect` + instant scroll avoids a
   * race where smooth scroll runs while the quiz unmounts and ends mid-page.
   */
  useLayoutEffect(() => {
    if (phase !== 'results' || results === null) return;
    scrollToDocumentTopImmediate();
  }, [phase, results]);

  const switchSpecies = (next: 'Cat' | 'Dog') => {
    setPetType(next);
    setProfile(defaultUserMatchProfile());
    setResults(null);
    setMatchApiPayload(null);
    setActionStatus(null);
    setCurrentStepIndex(0);
    setLastMatchResultsId(null);
  };

  const commitSpeciesAndEnterQuiz = (next: 'Cat' | 'Dog') => {
    switchSpecies(next);
    setPhase('quiz');
    trackQuizStart(next, shelterIdForMatch);
  };

  const canSubmitMatch =
    !isLoadingQuestions && !questionsError && (speciesPairs?.length ?? 0) > 0;

  const allWizardDimensionsAnswered = useMemo(() => {
    if (!speciesPairs?.length) return false;
    return speciesPairs.every((p) => isQuizStepComplete(p, profile));
  }, [speciesPairs, profile]);

  const setImportance = (key: DimensionKey, importance: ImportanceLevel) => {
    setProfile((prev) => ({
      ...prev,
      [key]: { ...prev[key], importance },
    }));
  };

  const setDetailLevel = (key: DimensionKey, detailLevel: number) => {
    setProfile((prev) => ({
      ...prev,
      [key]: { ...prev[key], detailLevel },
    }));
  };

  const handleFindMatches = () => {
    if (!speciesPairs?.length) return;
    const apiProfile = buildMatchApiProfile(profile, speciesPairs);
    if (!apiProfile) {
      setActionStatus('Complete each question before finding matches.');
      return;
    }
    setActionStatus(null);
    matchMutation.mutate(
      {
        species: petType,
        limit: 24,
        profile: apiProfile,
        ...(shelterIdForMatch ? { shelterId: shelterIdForMatch } : {}),
      },
      {
        onSuccess: (data) => {
          setMatchApiPayload(data);
          setLastMatchResultsId(data.matchResultsId ?? null);
          setResults(
            data.matches.map((m) => ({
              animal: m.animal,
              matchPercent: m.matchPercent,
              weightedPenalty: m.weightedPenalty,
            })),
          );
          setPhase('results');
          setActionStatus(null);
          trackQuizComplete(petType, data.matches.length, shelterIdForMatch);
          trackQuizPreferencesFromProfile(petType, apiProfile, shelterIdForMatch);
        },
        onError: (e) => {
          setMatchApiPayload(null);
          setResults(null);
          setLastMatchResultsId(null);
          setActionStatus(getMatchApiErrorMessage(e));
        },
      },
    );
  };

  const matchResponsePayload = useMemo(() => {
    if (matchApiPayload) {
      return {
        ...matchApiPayload,
        matches: matchApiPayload.matches.map((m) => ({
          rank: m.rank,
          matchPercent: m.matchPercent,
          weightedPenalty: m.weightedPenalty,
          animal: {
            id: m.animal.ID,
            name: m.animal.Name,
            type: m.animal.Type,
            sex: m.animal.Sex,
            breed: m.animal.Breed,
            coverPhoto: m.animal.CoverPhoto,
            profileUrl: m.animal.ProfileUrl,
            behaviorVector: animalBehaviorVector(m.animal),
            attributesRaw: m.animal.Attributes.map((a) => ({
              internalId: a['Internal-ID'],
              attributeName: a.AttributeName,
              publish: a.Publish,
            })),
          },
        })),
      };
    }
    return {
      schema: 'matching-poc.response.v2' as const,
      generatedAt: new Date().toISOString(),
      speciesFilter: petType,
      questionSource: questionsMeta
        ? `${questionsMeta.schema} · ${questionsMeta.sourceBundle}`
        : 'GET /match/questions (loading or error)',
      preferences: profileToSerializable(profile),
      maxWeightedPenalty: 0,
      matches: null as null,
      meta: {
        matchCount: 0,
        scoring:
          'D_total = sum_i w_i * |U_i - A_i|^2.32. Match % = round(max(0, (1 - D_total / (9.09 * sum_i w_i)) * 100)).',
        note: 'Run “Find matches” for POST /match. `matches`, `maxWeightedPenalty`, and match % are computed on the server only.',
      },
    };
  }, [matchApiPayload, petType, profile, questionsMeta]);

  const responseJson = useMemo(
    () => JSON.stringify(matchResponsePayload, null, 2),
    [matchResponsePayload],
  );

  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'error'>(
    'idle',
  );

  const handleCopyJson = async () => {
    try {
      await navigator.clipboard.writeText(responseJson);
      setCopyState('copied');
      window.setTimeout(() => setCopyState('idle'), 2000);
    } catch {
      setCopyState('error');
      window.setTimeout(() => setCopyState('idle'), 2500);
    }
  };

  const isLastStep =
    totalSteps > 0 && currentStepIndex >= totalSteps - 1 && currentPair != null;

  const currentDimensionAnswered = currentPair
    ? isQuizStepComplete(currentPair, profile)
    : false;

  /**
   * Auto-advance only when the active step transitions from incomplete -> complete.
   * This prevents immediately bouncing away when revisiting an already-answered step.
   */
  const prevStepCompletionRef = useRef<{
    stepKey: string | null;
    wasComplete: boolean;
  }>({
    stepKey: null,
    wasComplete: false,
  });
  useEffect(() => {
    if (phase !== 'quiz' || !currentPair) {
      prevStepCompletionRef.current = {
        stepKey: null,
        wasComplete: false,
      };
      return;
    }

    const stepKey = `${petType}-${currentPair.dimensionKey}-${currentStepIndex}`;
    const prev = prevStepCompletionRef.current;

    if (prev.stepKey !== stepKey) {
      prevStepCompletionRef.current = {
        stepKey,
        wasComplete: currentDimensionAnswered,
      };
      return;
    }

    const justCompleted = !prev.wasComplete && currentDimensionAnswered;
    prevStepCompletionRef.current = {
      stepKey,
      wasComplete: currentDimensionAnswered,
    };

    if (
      !autoStepComplete ||
      !justCompleted ||
      isLastStep ||
      matchMutation.isPending
    ) {
      return;
    }

    const frame = requestAnimationFrame(() => {
      setCurrentStepIndex((i) => Math.min(i + 1, Math.max(0, totalSteps - 1)));
    });
    return () => cancelAnimationFrame(frame);
  }, [
    phase,
    petType,
    currentPair,
    currentStepIndex,
    currentDimensionAnswered,
    isLastStep,
    totalSteps,
    matchMutation.isPending,
  ]);

  const handleFooterPrimary = () => {
    if (isLastStep) {
      handleFindMatches();
      return;
    }
    setCurrentStepIndex((i) => Math.min(i + 1, Math.max(0, totalSteps - 1)));
  };

  const handleFooterBack = () => {
    if (currentStepIndex === 0) {
      setPhase('species');
      return;
    }
    setCurrentStepIndex((i) => Math.max(0, i - 1));
  };

  /** Public shelter quiz — return to species selection with a clean profile. */
  const handleQuizRestart = () => {
    setPetType('Dog');
    setProfile(defaultUserMatchProfile());
    setResults(null);
    setMatchApiPayload(null);
    setActionStatus(null);
    setCurrentStepIndex(0);
    setLastMatchResultsId(null);
    setPhase('species');
    scrollToDocumentTopImmediate();
  };

  const handleResultsBack = () => {
    setPhase('quiz');
    setResults(null);
    setMatchApiPayload(null);
    setLastMatchResultsId(null);
    setActionStatus(null);
  };

  /** Full restart from results — species gate with a clean profile for another complete run. */
  const handleResultsRestart = () => {
    setProfile(defaultUserMatchProfile());
    setResults(null);
    setMatchApiPayload(null);
    setActionStatus(null);
    setCurrentStepIndex(0);
    setLastMatchResultsId(null);
    setPhase('species');
    scrollToDocumentTopImmediate();
  };

  const detailGroupName = currentPair
    ? `match-poc-detail-${petType}-${currentPair.dimensionKey}`
    : 'match-poc-detail';
  const importanceGroupName = currentPair
    ? `match-poc-importance-${petType}-${currentPair.dimensionKey}`
    : 'match-poc-importance';

  const detailQuestionId = 'match-poc-detail-question';
  const importanceLabelId = 'match-poc-importance-label';

  return (
    <>
      {phase === 'species' ? (
        <MatchQuizSpeciesGate
          onSelectSpecies={commitSpeciesAndEnterQuiz}
          shelterContextNote={shelterSpeciesGateNote}
        />
      ) : null}

      {phase === 'results' && results !== null ? (
        <>
          <MatchQuizResultsView
            petType={petType}
            results={results}
            shareMatchResultsId={lastMatchResultsId}
            onBack={handleResultsBack}
            onRestart={handleResultsRestart}
            shelterId={shelterIdForMatch}
            adoptionApplicationUrl={shelterMatchContext?.shelterAdoptionFormUrl}
            emptyRosterMessage={
              shelterIdForMatch
                ? `This shelter has no available ${petType.toLowerCase()} pets with complete match profiles in our system yet. Try the other species, or check back later.`
                : null
            }
          />
          {showMatchPocDebugPanel ? (
            <div className='border-t border-border bg-background'>
              <section
                className='mx-auto max-w-4xl space-y-4 p-4 sm:p-6 lg:p-8'
                aria-labelledby='matching-poc-response-json-heading-results'
              >
                <Divider decorative />
                <h2
                  id='matching-poc-response-json-heading-results'
                  className='font-heading text-lg font-semibold text-foreground'
                >
                  Response JSON (debug)
                </h2>
                <p className='text-sm text-muted-foreground'>
                  Shown when{' '}
                  <code className='rounded bg-accent px-1'>NODE_ENV</code> is
                  development or{' '}
                  <code className='rounded bg-accent px-1'>
                    NEXT_PUBLIC_MATCH_POC_DEBUG=true
                  </code>
                  .
                </p>
                <div className='flex flex-wrap gap-2'>
                  <Button
                    type='button'
                    variant='secondary'
                    onClick={handleCopyJson}
                  >
                    {copyState === 'copied'
                      ? 'Copied!'
                      : copyState === 'error'
                        ? 'Copy failed — select text below'
                        : 'Copy JSON to clipboard'}
                  </Button>
                </div>
                {copyState === 'error' ? (
                  <p className='text-sm text-red-600'>
                    Clipboard was blocked. Select the JSON in the box and copy
                    manually (⌘C / Ctrl+C).
                  </p>
                ) : null}
                <div className='max-h-[min(70vh,32rem)] overflow-auto rounded-xl border border-border bg-background'>
                  <pre
                    className='p-4 font-mono text-xs leading-relaxed text-foreground'
                    tabIndex={0}
                  >
                    {responseJson}
                  </pre>
                </div>
              </section>
            </div>
          ) : null}
        </>
      ) : null}

      {phase === 'quiz' ? (
        <div className='mx-auto space-y-10 pb-32'>
          <section className='p-4 sm:p-6 lg:p-8'>
            <div className='flex flex-wrap items-start justify-between gap-4'>
              {shelterMatchContext ? (
                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  onClick={handleQuizRestart}
                >
                  Cancel
                </Button>
              ) : (
                <Link
                  href={ROUTES.home}
                  className={cn(
                    buttonVariants({ variant: 'outline', size: 'sm' }),
                    'no-underline',
                  )}
                >
                  Cancel
                </Link>
              )}
              <div className='min-w-0 flex-1 text-right sm:text-left'>
                <h2 className='font-heading text-xl font-semibold tracking-tight text-foreground sm:text-2xl'>
                  Let&apos;s find a {petType}
                </h2>
                {shelterIdForMatch ? (
                  <p className='mt-2 text-sm text-muted-foreground'>
                    Results include only this shelter&apos;s available pets in
                    our system.
                  </p>
                ) : null}
              </div>
            </div>

            {totalSteps > 0 ? (
              <div className='mt-6'>
                <MatchQuizProgress
                  currentStep={currentStepIndex + 2}
                  totalSteps={totalSteps + 1}
                />
              </div>
            ) : null}

            {isLoadingQuestions ? (
              <p
                className='mt-6 text-sm font-medium text-muted-foreground'
                role='status'
                aria-live='polite'
              >
                Loading questions…
              </p>
            ) : null}

            {questionsError ? (
              <div
                className='mt-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900'
                role='alert'
              >
                <p className='font-medium'>Could not load questions</p>
                <p className='mt-1'>{questionsError}</p>
                <Button
                  type='button'
                  variant='secondary'
                  size='sm'
                  className='mt-3'
                  isLoading={questionsQuery.isFetching}
                  onClick={() => void questionsQuery.refetch()}
                >
                  Retry
                </Button>
              </div>
            ) : null}

            {currentPair && !questionsError ? (
              <div className='mt-8 lg:grid lg:grid-cols-2 lg:items-start lg:gap-10'>
                <div className='hidden lg:block'>
                  <MatchQuizStepIllustration />
                </div>

                <div className='min-w-0 space-y-8'>
                  <div className='scroll-mb-28'>
                    <div
                      id={`match-poc-detail-anchor-${petType}-${currentPair.dimensionKey}`}
                      className='scroll-mt-28 sm:scroll-mt-32'
                    >
                      <p
                        id={detailQuestionId}
                        className='font-heading text-base font-semibold leading-snug text-foreground sm:text-lg'
                      >
                        {currentPair.detailPrompt}
                      </p>
                    </div>
                    <div
                      className='mt-4 space-y-3'
                      role='radiogroup'
                      aria-labelledby={detailQuestionId}
                    >
                      {currentPair.detailOptions.map((opt, idx) => {
                        const { title, description } =
                          matchQuizOptionTitleDescription(opt.text);
                        const checked =
                          profile[currentPair.dimensionKey].detailLevel ===
                          opt.level;
                        return (
                          <MatchQuizOptionCard
                            key={opt.level}
                            name={detailGroupName}
                            optionValue={String(opt.level)}
                            checked={checked}
                            onSelect={() =>
                              setDetailLevel(
                                currentPair.dimensionKey,
                                opt.level,
                              )
                            }
                            title={title}
                            description={description}
                            optionIndex={idx}
                            showLeadingIcon
                          />
                        );
                      })}
                    </div>
                  </div>

                  <div className='scroll-mb-28'>
                    <div
                      id={`match-poc-importance-anchor-${petType}-${currentPair.dimensionKey}`}
                      className='scroll-mt-28 sm:scroll-mt-32'
                    >
                      <p
                        id={importanceLabelId}
                        className='text-sm font-semibold text-foreground'
                      >
                        {currentPair.importancePrompt}
                      </p>
                    </div>
                    <ImportanceSegmentedControl
                      name={importanceGroupName}
                      labelId={importanceLabelId}
                      value={profile[currentPair.dimensionKey].importance}
                      onChange={(v) =>
                        setImportance(currentPair.dimensionKey, v)
                      }
                      disabled={isLoadingQuestions}
                      className='mt-3'
                    />
                  </div>
                </div>
              </div>
            ) : null}

            {currentPair && !questionsError ? (
              <MatchQuizFooterNav
                className='mt-10 sm:rounded-b-2xl'
                primaryButtonId={MATCH_POC_QUIZ_FOOTER_PRIMARY_ID}
                onBack={handleFooterBack}
                onPrimary={handleFooterPrimary}
                primaryLabel={isLastStep ? 'Find matches →' : 'Next step →'}
                primaryDisabled={
                  isLastStep
                    ? !allWizardDimensionsAnswered ||
                      !canSubmitMatch ||
                      matchMutation.isPending
                    : !currentDimensionAnswered
                }
                isPrimaryLoading={
                  matchMutation.isPending && Boolean(isLastStep)
                }
              />
            ) : null}

            {/* <div className='mt-8 flex flex-wrap gap-3 border-t border-border pt-6'>
              <Button
                type='button'
                variant='secondary'
                onClick={handleReset}
                disabled={matchMutation.isPending || isLoadingQuestions}
              >
                Reset answers
              </Button>
            </div> */}

            {actionStatus ? (
              <p
                className='mt-4 rounded-lg border border-primary-600/25 bg-primary-50 px-4 py-3 text-sm font-medium text-primary-900'
                role='status'
                aria-live='polite'
              >
                {actionStatus}
              </p>
            ) : null}
          </section>

          {showMatchPocDebugPanel ? (
            <>
              <Divider className='my-8' decorative />
              <section
                className='rounded-2xl border border-border bg-card p-4 shadow-sm sm:p-6'
                aria-labelledby='matching-poc-response-json-heading'
              >
                <h2
                  id='matching-poc-response-json-heading'
                  className='font-heading text-lg font-semibold text-foreground'
                >
                  Response JSON (debug)
                </h2>
                <p className='mt-1 text-sm text-muted-foreground'>
                  Shown when{' '}
                  <code className='rounded bg-accent px-1'>NODE_ENV</code> is
                  development or{' '}
                  <code className='rounded bg-accent px-1'>
                    NEXT_PUBLIC_MATCH_POC_DEBUG=true
                  </code>
                  . After <strong>Find matches</strong>, this mirrors the API
                  response.
                </p>
                <div className='mt-4 flex flex-wrap gap-2'>
                  <Button
                    type='button'
                    variant='secondary'
                    onClick={handleCopyJson}
                  >
                    {copyState === 'copied'
                      ? 'Copied!'
                      : copyState === 'error'
                        ? 'Copy failed — select text below'
                        : 'Copy JSON to clipboard'}
                  </Button>
                </div>
                {copyState === 'error' ? (
                  <p className='mt-2 text-sm text-red-600'>
                    Clipboard was blocked. Select the JSON in the box and copy
                    manually (⌘C / Ctrl+C).
                  </p>
                ) : null}
                <div className='mt-4 max-h-[min(70vh,32rem)] overflow-auto rounded-xl border border-border bg-background'>
                  <pre
                    className='p-4 font-mono text-xs leading-relaxed text-foreground'
                    tabIndex={0}
                  >
                    {responseJson}
                  </pre>
                </div>
              </section>
            </>
          ) : null}
        </div>
      ) : null}
    </>
  );
}
